import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Phone, Instagram, Facebook } from "lucide-react";
import { NAV_LINKS, WA_DEFAULT, WHATSAPP_DISPLAY, INSTAGRAM_URL, FACEBOOK_URL } from "../lib/site";

export const scrollToSection = (e, href) => {
  e.preventDefault();
  if (href.startsWith("/")) {
    window.location.href = href;
    return;
  }
  if (window.location.pathname !== "/") {
    window.location.href = "/" + href;
    return;
  }
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -130, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50"
    >
      <div className="hidden lg:block bg-white border-b border-brand/10" data-testid="header-topbar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-12 flex items-center justify-center gap-5">
          <a
            href="tel:+5545998203513"
            data-testid="topbar-phone"
            className="inline-flex items-center gap-2.5 text-sm font-extrabold text-brand-deep transition-colors duration-300 hover:text-accent"
          >
            <span className="grid place-items-center w-8 h-8 rounded-full bg-brand text-white">
              <Phone size={15} />
            </span>
            Reservas: {WHATSAPP_DISPLAY}
          </a>
          <span className="h-5 w-px bg-brand/15" aria-hidden="true" />
          <div className="flex items-center gap-2.5">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-instagram"
              aria-label="Instagram do Magnus Máximo"
              className="grid place-items-center w-8 h-8 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white shadow-sm transition-transform duration-300 hover:scale-110"
            >
              <Instagram size={15} />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-facebook"
              aria-label="Facebook do Magnus Máximo"
              className="grid place-items-center w-8 h-8 rounded-full bg-[#1877F2] text-white shadow-sm transition-transform duration-300 hover:scale-110"
            >
              <Facebook size={15} />
            </a>
          </div>
        </div>
      </div>
      <div
        className={`transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm"
            : "bg-transparent border-b border-transparent"
        }`}
      >
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-6">
        <a
          href="#inicio"
          onClick={(e) => scrollToSection(e, "#inicio")}
          data-testid="header-logo"
          className="flex items-center max-lg:absolute max-lg:left-1/2 max-lg:-translate-x-1/2"
        >
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cataratas-experienza/artifacts/46i9plwr_WhatsApp%20Image%202026-09-11%20at%2009.27.52.png"
            alt="Magnus Máximo — Guia de Turismo em Foz do Iguaçu"
            className="h-20 md:h-28 w-auto object-contain rounded-2xl bg-white px-3 py-1.5 shadow-sm"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollToSection(e, l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className={`text-[13px] font-semibold tracking-wide transition-colors duration-300 hover:text-accent ${
                scrolled ? "text-ink/80" : "text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 ml-auto">
          <span className="flex lg:hidden items-center gap-2.5 max-lg:absolute max-lg:left-5 max-lg:top-[46px] max-lg:-translate-y-1/2">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Instagram do Magnus Máximo"
            className={`grid place-items-center w-9 h-9 rounded-full transition-colors duration-300 hover:text-accent ${
              scrolled ? "text-brand-deep bg-brand/5" : "text-white bg-white/15 backdrop-blur"
            }`}
          >
            <Instagram size={16} />
          </a>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-facebook"
            aria-label="Facebook do Magnus Máximo"
            className={`grid place-items-center w-9 h-9 rounded-full transition-colors duration-300 hover:text-accent ${
              scrolled ? "text-brand-deep bg-brand/5" : "text-white bg-white/15 backdrop-blur"
            }`}
          >
            <Facebook size={16} />
          </a>
          </span>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-whatsapp px-5 py-2.5 text-[13px] font-bold text-white shadow-lg shadow-whatsapp/30 transition-transform duration-300 hover:scale-[1.04]"
          >
            <MessageCircle size={16} />
            WhatsApp
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menu"
            className={`lg:hidden grid place-items-center w-10 h-10 rounded-full ${
              scrolled ? "text-brand-deep bg-brand/5" : "text-white bg-white/15 backdrop-blur"
            }`}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-brand/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  onClick={(e) => {
                    setOpen(false);
                    scrollToSection(e, l.href);
                  }}
                  className="py-3 text-base font-semibold text-ink border-b border-brand/5 last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 text-sm font-bold text-white"
              >
                <MessageCircle size={18} />
                Falar com o Magnus
              </a>
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-sand px-5 py-3.5">
                <a href="tel:+5545998203513" data-testid="mobile-menu-phone" className="inline-flex items-center gap-2 text-sm font-bold text-brand-deep">
                  <Phone size={15} />
                  Reservas: {WHATSAPP_DISPLAY}
                </a>
                <div className="flex gap-2">
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-instagram" aria-label="Instagram" className="grid place-items-center w-9 h-9 rounded-full bg-brand/10 text-brand">
                    <Instagram size={16} />
                  </a>
                  <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-facebook" aria-label="Facebook" className="grid place-items-center w-9 h-9 rounded-full bg-brand/10 text-brand">
                    <Facebook size={16} />
                  </a>
                </div>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
